import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { Card, Button, Form, Input, Alert, Typography, Space, Spin, Steps, Result, Progress, Tooltip } from 'antd';
import { UserOutlined, SafetyCertificateOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { validateRegistrationToken, registerUser, getRegisterOptions, verifyRegistration } from '../lib/api';

const { Title, Text } = Typography;

// Password strength indicator
function PasswordStrength({ password }: { password: string }) {
  const calculateStrength = (pwd: string): number => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (pwd.length >= 12) strength += 10;
    if (/[A-Z]/.test(pwd)) strength += 20;
    if (/[a-z]/.test(pwd)) strength += 20;
    if (/[0-9]/.test(pwd)) strength += 25;
    return Math.min(strength, 100);
  };

  const getStrengthColor = (score: number): string => {
    if (score < 40) return '#ff4d4f';
    if (score < 70) return '#faad14';
    return '#52c41a';
  };

  const getStrengthText = (score: number): string => {
    if (score < 40) return '弱';
    if (score < 70) return '中';
    return '強';
  };

  const strength = calculateStrength(password);

  if (!password) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Progress
        percent={strength}
        size="small"
        strokeColor={getStrengthColor(strength)}
        showInfo={false}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        密碼強度: <span style={{ color: getStrengthColor(strength) }}>{getStrengthText(strength)}</span>
      </Text>
    </div>
  );
}

// Password requirements checklist
function PasswordRequirements({ password }: { password: string }) {
  const requirements = [
    { label: '至少 8 個字符', valid: password.length >= 8 },
    { label: '包含大寫字母 (A-Z)', valid: /[A-Z]/.test(password) },
    { label: '包含小寫字母 (a-z)', valid: /[a-z]/.test(password) },
    { label: '包含數字 (0-9)', valid: /[0-9]/.test(password) },
  ];

  return (
    <ul style={{ paddingLeft: 20, margin: '8px 0', fontSize: 13 }}>
      {requirements.map((req, idx) => (
        <li key={idx} style={{ color: req.valid ? '#52c41a' : '#999' }}>
          {req.valid ? '✓' : '○'} {req.label}
        </li>
      ))}
    </ul>
  );
}

export default function RegisterWithLink() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [form] = Form.useForm();

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [userId, setUserId] = useState<number | null>(null);
  const [passwordValue, setPasswordValue] = useState('');
  const [skipPasskey, setSkipPasskey] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setValidationError('缺少註冊連結憑證');
      return;
    }

    validateRegistrationToken(token)
      .then((data) => {
        setIsValidating(false);
        if (data.valid) {
          setIsValid(true);
        } else {
          setValidationError(data.error || '無效的註冊連結');
        }
      })
      .catch(() => {
        setIsValidating(false);
        setValidationError('驗證連結時發生錯誤');
      });
  }, [token]);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/[0-9]/.test(password)) return false;
    return true;
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setError('');

    // Validate password
    if (!validatePassword(values.password)) {
      setError('密碼不符合要求');
      setLoading(false);
      return;
    }

    // Confirm password match
    if (values.password !== values.confirmPassword) {
      setError('兩次輸入的密碼不一致');
      setLoading(false);
      return;
    }

    try {
      const result = await registerUser({
        email: values.email || undefined,
        username: values.username,
        password: values.password,
        name: values.name || undefined,
        token: token || undefined,
        role: 'employee',
      });

      setUserId(result.userId);
      setCurrentStep(1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterPasskey = async () => {
    if (!userId) return;

    setLoading(true);
    setError('');

    try {
      const formData = form.getFieldsValue();
      const options = await getRegisterOptions(userId, formData.email || '');

      const challengeId = options.challengeId;
      const { challengeId: _, ...webauthnOptions } = options;

      const attestationResponse = await startRegistration(webauthnOptions);

      await verifyRegistration({
        userId,
        challengeId,
        response: attestationResponse,
      });

      setCurrentStep(2);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPasskey = () => {
    setSkipPasskey(true);
    setCurrentStep(2);
  };

  if (isValidating) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16 }}>驗證中...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Alert
            title="無效的連結"
            description={
              <Space orientation="vertical">
                <Text>{validationError}</Text>
                <Text>此註冊連結可能已過期或達到使用次數上限。</Text>
                <Text>請聯繫管理員取得新的註冊連結。</Text>
                <Link to="/login">
                  <Button type="primary">返回登入頁面</Button>
                </Link>
              </Space>
            }
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="註冊完成！"
            subTitle={skipPasskey
              ? '您的帳號已成功建立，您可以在登入後於個人設定中綁定 Passkey'
              : '您的帳號和 Passkey 已成功設定'}
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">前往登入</Button>
              </Link>,
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ width: '100%', maxWidth: 520, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <Steps current={currentStep} items={[
            { title: '填寫資料', icon: <UserOutlined /> },
            { title: '設定 Passkey', icon: <SafetyCertificateOutlined /> },
            { title: '完成', icon: <CheckCircleOutlined /> },
          ]} />

          {error && <Alert title={error} type="error" showIcon closable />}

          {currentStep === 0 ? (
            <>
              <Title level={3} style={{ textAlign: 'center', margin: 0 }}>註冊新帳號</Title>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="username"
                  label="帳號名"
                  rules={[
                    { required: true, message: '請輸入帳號名' },
                    { pattern: /^[a-zA-Z0-9_-]+$/, message: '帳號名只能包含英文、數字、底線和連字符' },
                  ]}
                >
                  <Input placeholder="用於登入的帳號名" autoComplete="username" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={
                    <Tooltip title="密碼必須至少 8 位，包含大小寫英文字母和數字">
                      <span>密碼 <Text type="secondary">(?)</Text></span>
                    </Tooltip>
                  }
                  rules={[{ required: true, message: '請輸入密碼' }]}
                >
                  <Input.Password
                    placeholder="設定密碼"
                    autoComplete="new-password"
                    onChange={(e) => setPasswordValue(e.target.value)}
                  />
                </Form.Item>

                {passwordValue && (
                  <Form.Item style={{ marginBottom: 8 }}>
                    <PasswordStrength password={passwordValue} />
                    <PasswordRequirements password={passwordValue} />
                  </Form.Item>
                )}

                <Form.Item
                  name="confirmPassword"
                  label="確認密碼"
                  rules={[
                    { required: true, message: '請再次輸入密碼' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('兩次輸入的密碼不一致'));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="再次輸入密碼" autoComplete="new-password" />
                </Form.Item>

                <Form.Item name="email" label="電子郵件">
                  <Input type="email" placeholder="user@example.com (選填)" />
                </Form.Item>

                <Form.Item name="name" label="姓名">
                  <Input placeholder="您的姓名 (選填)" />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} block size="large">
                    建立帳號
                  </Button>
                </Form.Item>
              </Form>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">已有帳號？ <Link to="/login">立即登入</Link></Text>
              </div>
            </>
          ) : (
            <>
              <Title level={3} style={{ textAlign: 'center', margin: 0 }}>設定 Passkey（可選）</Title>
              <Alert
                title="什麼是 Passkey？"
                description={
                  <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                    <li>使用裝置的指紋辨識或臉部辨識快速登入</li>
                    <li>無需輸入密碼，更加安全便捷</li>
                    <li>可以稍後在個人設定中綁定</li>
                  </ul>
                }
                type="info"
                showIcon
              />
              <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleRegisterPasskey}
                  loading={loading}
                  icon={<SafetyCertificateOutlined />}
                >
                  綁定 Passkey
                </Button>
                <Button
                  size="large"
                  block
                  onClick={handleSkipPasskey}
                  disabled={loading}
                >
                  稍後再說，直接完成註冊
                </Button>
              </Space>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
